import { LINK_REQUEST_EXPIRED_MESSAGE } from '#~/api/agent/link-request-expiry.js';
import { useEffect, useState } from 'react';

/** A new route reads the same server deadline; navigating never restarts 30 minutes. */
export const useLinkRequestLifetime = (deadline: number | undefined, error?: { code?: number }) => {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    if (deadline === undefined) return;
    const remaining = deadline - Date.now();
    if (remaining <= 0 && now >= deadline) return;
    const timer = setTimeout(() => setNow(Date.now()), Math.max(0, Math.min(1000, remaining)));
    return () => clearTimeout(timer);
  }, [deadline, now]);
  return {
    expired:
      error?.code === 412 || (deadline !== undefined && Math.max(now, Date.now()) >= deadline),
    remainingMs:
      deadline === undefined ? undefined : Math.max(0, deadline - Math.max(now, Date.now()))
  };
};

export const LinkRequestLifetimeNotice = ({
  expired,
  remainingMs
}: {
  expired: boolean;
  remainingMs: number | undefined;
}) => {
  if (expired)
    return (
      <div role='alert' className='p-16 text-center'>
        <h1 className='text-lg font-bold'>Link expired</h1>
        <p>{LINK_REQUEST_EXPIRED_MESSAGE}</p>
      </div>
    );
  if (remainingMs === undefined) return null;
  // Approval must finish before expiry; show the final seconds rather than
  // suggesting a whole minute remains when the request is about to close.
  const unit = remainingMs < 60000 ? 'second' : 'minute';
  const remaining = Math.ceil(remainingMs / (unit === 'second' ? 1000 : 60000));
  return (
    <p role='status' className='text-sm'>
      This link expires in {remaining} {unit}
      {remaining === 1 ? '' : 's'}.
    </p>
  );
};
